import { FC } from "react";
import Image from "next/image";
import QRCode from "react-qr-code";

import { Button } from "@/components/common";
import { Send } from "@/components/icons";

import { Receipt, ExtendedViewItem } from "@/types/receipt";

import { translate, formatDate } from "@/utils/formatters";
import { getActionableButtonText } from "@/utils/extendedViewHelpers";
import { useExtendedServiceHandler } from "@/hooks/useExtendedServiceHandler";

import styles from "./ReturnPopup.module.css";
import { useExtendedWidgetVisibility } from "@/hooks/useExtendedWidgetVisibility";
import { SharingWarning } from "./SharingWarning";

interface ReturnPopupProps {
  receipt: Receipt;
  lang: string;
  mainReceiptBarcodeImage: string;
  extendedViewItems: ExtendedViewItem[];
  extendedViewImages: { [key: string]: string };
  user_id?: string;
  receipt_id?: string;
  email?: string | null;
  phoneNumber?: string | null;
  closeButton?: React.ReactNode;
}

export const ReturnPopup: FC<ReturnPopupProps> = ({
  receipt,
  lang,
  mainReceiptBarcodeImage,
  extendedViewItems,
  extendedViewImages,
  user_id,
  receipt_id,
  email,
  closeButton,
  phoneNumber,
}) => {
  const { handleServiceClick, isLoading } = useExtendedServiceHandler({
    receipt,
    lang,
    user_id,
    receipt_id,
    email,
    phoneNumber,
  });

  const { returnWidget } = useExtendedWidgetVisibility({
    extended_view_items: extendedViewItems,
    extended_view_images: extendedViewImages,
    currentView: "return_view",
  });

  return (
    <>
      <div className="box head">
        <h4>{translate(lang, "Retur & Skann Kvittering", "Return & Scan Receipt")}</h4>
        {closeButton}
      </div>

      <div className="box">
        {receipt.extra_receipt_view?.return_policy?.description && (
          <div className={styles.section}>
            <h5>{translate(lang, "Åpent Kjøp", "Return policy")}</h5>
            <p>{receipt.extra_receipt_view?.return_policy?.description}</p>
          </div>
        )}

        {receipt.extra_receipt_view?.return_policy?.expired_at && (
          <div className={styles.section}>
            <h5>
              {translate(lang, "Siste dag for retur: ", "Last day of return: ")}
            </h5>
            <p className={styles.instructionText}>
              {formatDate(
                receipt.extra_receipt_view.return_policy.expired_at,
                receipt.merchant.country_code,
                true
              )}
            </p>
          </div>
        )}

        {receipt.extra_receipt_view?.barcode?.value && (
          <>
            <h5 className={styles.sectionTitle}>
              {translate(
                lang,
                "Skann for å returnere artikkel: ",
                "Scan to return article: "
              )}
            </h5>

            <div className={styles.qrContainer}>
              <div className={styles.qrWrapper}>
                <div>
                  {receipt.extra_receipt_view.barcode.encoding === "qr" ? (
                    <QRCode
                      value={receipt.extra_receipt_view.barcode.value}
                      size={140}
                      level="H"
                      title="Return QR code"
                    />
                  ) : (
                    <Image
                      src={mainReceiptBarcodeImage}
                      alt="Barcode"
                      width={260}
                      height={100}
                      className={styles.qrImage}
                    />
                  )}
                </div>
                {receipt.extra_receipt_view.barcode.display_value && (
                  <p className={styles.qrCode}>
                    {receipt.extra_receipt_view.barcode.display_value}
                  </p>
                )}
              </div>
            </div>
          </>
        )}

        {/* Return Service Section */}
        {returnWidget.length > 0 && (
          <div className={styles.servicesSection}>
            <h5 className={styles.sectionTitle}>
              {translate(lang, "Returservice", "Return Service")}
            </h5>

            {returnWidget.map((item) => {
              const itemKey = `return_service_${item.originalIndex}`;

              return (
                <div key={itemKey} className={styles.serviceCard}>
                  <div className={styles.serviceHeader}>
                    <div className={styles.serviceTitle}>{item.title}</div>
                    {item.imageSrc && (
                      <Image
                        src={item.imageSrc}
                        alt={item.title}
                        width={32}
                        height={32}
                        className={styles.serviceLogo}
                      />
                    )}
                  </div>

                  <div className={styles.serviceDescription}>
                    {item.description}
                  </div>

                  <SharingWarning
                    item={item}
                    email={email}
                    phoneNumber={phoneNumber}
                    lang={lang}
                  />

                  <Button
                    className={styles.actionButton}
                    onClick={() => handleServiceClick(item, itemKey)}
                    text={getActionableButtonText(
                      item.extended_view_position,
                      lang,
                      {
                        extended_receipt_view_button_text:
                          item.extended_receipt_view_button_text,
                      }
                    )}
                    isLoading={isLoading(itemKey)}
                    icon={<Send />}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};
